import type React from 'react';

import { FocusRing, Text } from '@change/ds-core-ui';
import { CDotIcon } from '@change/ds-icons/CDotIcon';
import { cn } from '@change/ds-utils';

import { useStaffDrawer } from './hooks/useStaffDrawer';

type Props = {
	children: React.ReactNode;
};

/**
 * Staff/support-only drawer pinned to the top-right of the viewport (desktop only).
 * The same toggle button opens and closes it; it sits below modals (`z-30`).
 *
 * The toggle and the panel slide together: when closed, the wrapper is translated by the panel's width
 * so only the toggle sticks out of the right edge.
 */
export const StaffDrawer: React.FC<Props> = ({ children }) => {
	const {
		data: { isVisible, isOpen, panelId, headingId },
		actions: { toggle },
	} = useStaffDrawer();

	if (!isVisible) {
		return null;
	}

	return (
		<div
			className={cn(
				'duration-t4 ease-inform fixed top-0 right-0 z-29 hidden items-start transition-transform motion-reduce:transition-none md:flex',
				isOpen ? 'translate-x-0' : 'translate-x-80',
			)}
			data-testid="staff-drawer"
		>
			{/* plain button: the ds Button forces `medium` icons and draws an inset `::before` border */}
			<FocusRing>
				<button
					type="button"
					aria-label="Staff tools"
					title="Staff tools"
					aria-expanded={isOpen}
					aria-controls={panelId}
					onClick={toggle}
					// `-mr-px` + `z-1` overlap the panel's left border so the toggle reads as a tab of the open drawer; the clip-path keeps its shadow off the panel
					className="bg-background-raised border-stroke-weak text-typography-weak hover:text-typography-strong relative z-1 mt-4 -mr-px inline-flex size-7 cursor-pointer items-center justify-center rounded-l-md border border-r-0 shadow-[-2px_2px_6px_rgba(0,0,0,0.2)] [clip-path:inset(-12px_0_-12px_-12px)]"
					data-testid="staff-drawer-toggle"
					data-qa="staff-drawer-toggle"
				>
					<CDotIcon size={16} />
				</button>
			</FocusRing>
			<section
				id={panelId}
				aria-labelledby={headingId}
				inert={!isOpen}
				className={cn(
					'bg-background-raised border-stroke-weak duration-t4 ease-inform max-h-screen w-80 overflow-y-auto rounded-bl-md border border-r-0 transition-shadow motion-reduce:transition-none',
					isOpen ? 'shadow-[-2px_2px_8px_rgba(0,0,0,0.2)]' : 'shadow-none',
				)}
				data-testid="staff-drawer-panel"
			>
				<Text
					as="h2"
					id={headingId}
					variant="heading"
					size="xx-small"
					className="text-typography-strong border-stroke-weak block border-b px-4 py-3"
				>
					Staff tools
				</Text>
				<div className="divide-stroke-weak flex flex-col divide-y">{children}</div>
			</section>
		</div>
	);
};
